<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <div class="d-flex mb-8 align-center">
        <h2 class="font-weight-bold">{{ $t('carbonCredit.manageTrees.addNewTree') }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined class="mr-3" style="border-color: #00bd73; color: #00bd73;" @click="cancel">
          {{ $t('carbonCredit.manageTrees.cancel') }}
        </v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ $t('carbonCredit.manageTrees.save') }}
        </v-btn>
      </div>

      <v-form ref="form" lazy-validation>
        <v-card flat class="pa-6">
          <v-row>
            <!-- Row 1 -->
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.idLabel') }}</label>
              <v-text-field v-model="form.tree_id_by_user" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.additionalId') }}</label>
              <v-text-field v-model="form.additional_id" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectFarm') }}</label>
              <v-autocomplete v-model="form.farm_id" :items="farms" item-text="farmName" item-value="id"
                :search-input.sync="farmSearchInput" outlined dense :loading="fetchingFarms"
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')" :no-filter="true" :hide-no-data="false"
                @focus="onFarmFocus" clearable @change="onFarmChange">
                <template v-slot:append-item>
                  <div v-if="fetchingMoreFarms" class="px-4 py-2 text-caption grey--text text-center">
                    Loading more farms...
                  </div>
                  <div v-else-if="farmsHasMore" v-intersect.quiet="onFarmListIntersect" style="height: 1px;"></div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectZone') }}</label>
              <v-select v-model="form.zone_id" :items="zones" item-text="geofenceName" item-value="id" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable
                :disabled="!form.farm_id"></v-select>
            </v-col>

            <!-- Row 2 -->
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.gpsCoordinates') }}</label>
              <v-text-field v-model="form.gps_coordinates" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')" :hint="$t('carbonCredit.manageTrees.gpsHint')"
                persistent-hint></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.altitude') }}</label>
              <v-text-field v-model="form.altitude" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.locationInformation') }}</label>
              <v-text-field v-model="form.location_information" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.seedlingName') }}</label>
              <v-text-field v-model="form.seedling_name" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectTreeType') }}</label>
              <v-select v-model="form.tree_type_id" :items="dropdown.treeTypes" item-text="name" item-value="id"
                outlined dense :loading="fetchingDropdowns" :placeholder="$t('carbonCredit.manageTrees.makeASelection')"
                clearable @change="form.tree_species_id = null"></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectTreeSpecies') }}</label>
              <v-select v-model="form.tree_species_id" :items="filteredTreeSpecies" item-text="name" item-value="id"
                outlined dense :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable
                :disabled="!form.tree_type_id"></v-select>
            </v-col>

            <!-- Row 3 -->
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.datePlanted') }}</label>
              <v-menu v-model="datePicker" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="form.date_planted" outlined dense readonly v-bind="attrs" v-on="on"
                    append-icon="mdi-calendar"></v-text-field>
                </template>
                <v-date-picker v-model="form.date_planted" @input="datePicker = false" no-title></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectHealthCondition') }}</label>
              <v-select v-model="form.health_condition" :items="dropdown.healthConditions" item-text="name"
                item-value="id" outlined dense :loading="fetchingDropdowns"
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.includeIn') }}</label>
              <v-select v-model="form.program_type" :items="dropdown.programTypes" item-text="name" item-value="id"
                outlined dense multiple :loading="fetchingDropdowns"
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable></v-select>
            </v-col>
          </v-row>

          <v-row>

            <!-- Additional Notes -->
            <v-col cols="12" md="6">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.additionalNotes') }}</label>
              <v-textarea v-model="form.additional_note" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"
                :rules="[rules.maxLength(500)]" maxlength="500" counter="500"
                rows="5" no-resize></v-textarea>
            </v-col>
          </v-row>

          <v-row>

            <!-- Attach Images -->
            <v-col cols="12" md="6">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.attachImages') }}</label>
              <div class="dropzone" @click="triggerFileInput" @dragover.prevent @drop.prevent="onFileDrop"
                @dragenter="dragging = true" @dragleave="dragging = false" :class="{ 'dropzone--active': dragging }">
                <v-icon size="40" color="#0057BD">mdi-tray-arrow-up</v-icon>
                <div class="mt-2">
                  <span class="blue--text text-decoration-underline" style="cursor: pointer;">
                    {{ $t('carbonCredit.manageTrees.selectFilesToUpload') }}
                  </span>
                </div>
                <div class="caption grey--text">{{ $t('carbonCredit.manageTrees.dragAndDrop') }}</div>
                <input ref="fileInput" type="file" multiple accept="image/*" style="display: none;"
                  @change="onFileChange" />
              </div>
              <div v-if="uploadedImages.length" class="d-flex flex-wrap mt-3">
                <div v-for="(img, idx) in uploadedImages" :key="idx" class="image-preview-item">
                  <v-img :src="img.url" width="80" height="80" cover class="rounded"></v-img>
                  <v-btn icon x-small class="remove-image-btn" @click="removeImage(idx)">
                    <v-icon small color="red">mdi-close-circle</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-form>
    </v-container>
  </div>
</template>

<script>
import CarbonCreditService from "@/_services/CarbonCreditService";
import { getAllFarmWithZones } from "@/_services/OptionService";
import S3UploadService from "@/_services/S3UploadService";
import loadingMixin from "@/mixins/LoadingMixin";

export default {
  name: "CreateTree",
  mixins: [loadingMixin],

  data() {
    return {
      fullPage: true,
      saving: false,
      datePicker: false,
      dragging: false,
      fetchingFarms: false,
      fetchingDropdowns: false,
      uploadingImages: false,
      uploadedImages: [],
      farmSearchInput: "",
      farmSearchDebounceTimer: null,
      farmsPage: 1,
      farmsLimit: 20,
      farmsHasMore: false,
      fetchingMoreFarms: false,
      activeFarmSearchTerm: "",
      form: {
        tree_id_by_user: "",
        additional_id: "",
        farm_id: null,
        zone_id: null,
        gps_coordinates: "",
        location_information: null,
        altitude: "",
        seedling_name: "",
        tree_type_id: null,
        tree_species_id: null,
        date_planted: new Date().toISOString().substr(0, 10),
        health_condition: null,
        program_type: [],
        additional_note: "",
      },
      allFarmsWithZones: [],
      zones: [],
      dropdown: {
        treeTypes: [],
        treeSpecies: [],
        healthConditions: [],
        programTypes: [],
      },
      rules: {
        required: v => !!v || this.$t('carbonCredit.manageTrees.fieldRequired'),
        maxLength: (max) => (v) =>
          !v || String(v).length <= max || `Maximum ${max} characters allowed`,
      },
      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard", exact: true },
        { text: "Carbon Credit", disabled: false, href: "/carbon-credit/projects" },
        { text: "Manage Trees", disabled: false, href: "/carbon-credit/manage-trees" },
        { text: "Add New Tree", disabled: true },
      ],
    };
  },

  computed: {
    farms() {
      return this.allFarmsWithZones.map(f => ({
        ...f,
        farmName: f.farmName || `#${f.id}`,
      }));
    },
    filteredTreeSpecies() {
      if (!this.form.tree_type_id) return this.dropdown.treeSpecies;
      return this.dropdown.treeSpecies.filter(
        s => String(s.tree_type_id) === String(this.form.tree_type_id)
      );
    },
  },

  created() {
    this.fetchFarmsBySearch("", { allowEmptySearch: true });
    this.fetchDropdowns();
  },

  beforeDestroy() {
    if (this.farmSearchDebounceTimer) {
      clearTimeout(this.farmSearchDebounceTimer);
      this.farmSearchDebounceTimer = null;
    }
  },

  watch: {
    farmSearchInput(newValue) {
      if (this.farmSearchDebounceTimer) clearTimeout(this.farmSearchDebounceTimer);
      this.farmSearchDebounceTimer = setTimeout(() => {
        this.fetchFarmsBySearch(newValue);
      }, 400);
    },
  },

  methods: {
    async fetchFarmsBySearch(
      searchTerm = "",
      { allowEmptySearch = false, append = false } = {}
    ) {
      const normalizedSearch = String(searchTerm || "").trim();
      if (!allowEmptySearch && normalizedSearch.length > 0 && normalizedSearch.length < 2) {
        return;
      }

      const nextPage = append ? this.farmsPage + 1 : 1;
      if (append && (!this.farmsHasMore || this.fetchingMoreFarms)) return;

      if (append) {
        this.fetchingMoreFarms = true;
      } else {
        this.fetchingFarms = true;
      }
      try {
        const farmList = await getAllFarmWithZones({
          search: normalizedSearch,
          limit: this.farmsLimit,
          page: nextPage,
        });
        const incoming = Array.isArray(farmList) ? farmList : [];

        if (append) {
          const existingIds = new Set(this.allFarmsWithZones.map((farm) => farm.id));
          const dedupedIncoming = incoming.filter((farm) => !existingIds.has(farm.id));
          this.allFarmsWithZones = [...this.allFarmsWithZones, ...dedupedIncoming];
        } else {
          this.allFarmsWithZones = incoming;
        }

        this.farmsPage = nextPage;
        this.farmsHasMore = incoming.length >= this.farmsLimit;
        this.activeFarmSearchTerm = normalizedSearch;
      } catch (err) {
        console.error("Error fetching farms", err);
      } finally {
        this.fetchingFarms = false;
        this.fetchingMoreFarms = false;
      }
    },

    onFarmFocus() {
      this.farmSearchInput = "";
      this.fetchFarmsBySearch("", { allowEmptySearch: true });
    },

    onFarmListIntersect(entries, observer, isIntersecting) {
      if (!isIntersecting) return;
      this.fetchFarmsBySearch(this.activeFarmSearchTerm, {
        allowEmptySearch: true,
        append: true,
      });
    },

    async fetchDropdowns() {
      this.fetchingDropdowns = true;
      try {
        const res = await CarbonCreditService.getTreeDropdownValues();
        const data = res.data || res;
        this.dropdown.treeTypes = data.treeTypes || [];
        this.dropdown.treeSpecies = data.treeSpecies || [];
        this.dropdown.healthConditions = data.healthConditions || [];
        this.dropdown.programTypes = data.programTypes || [];
      } catch (err) {
        console.error("Error fetching dropdowns", err);
      } finally {
        this.fetchingDropdowns = false;
      }
    },

    onFarmChange() {
      this.form.zone_id = null;
      this.farmSearchInput = "";
      const selectedFarm = this.allFarmsWithZones.find(
        f => String(f.id) === String(this.form.farm_id)
      );
      const farmSegments = selectedFarm?.segments || selectedFarm?.Geofences || [];
      if (Array.isArray(farmSegments)) {
        const nonPrimarySegments = farmSegments.filter(s => !s.isPrimary);
        const segmentsToUse = nonPrimarySegments.length ? nonPrimarySegments : farmSegments;
        this.zones = segmentsToUse.map(s => ({
          ...s,
          geofenceName: s.geofenceName || s.id,
        }));
      } else {
        this.zones = [];
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async onFileChange(event) {
      const files = Array.from(event.target.files);
      await this.processFiles(files);
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    async onFileDrop(event) {
      this.dragging = false;
      const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith("image/"));
      await this.processFiles(files);
    },

    async processFiles(files) {
      const imageFiles = (files || []).filter((f) => f && f.type?.startsWith("image/"));
      if (!imageFiles.length) return;

      this.uploadingImages = true;
      this.startLoading();
      try {
        for (const file of imageFiles) {
          try {
            const result = await S3UploadService.imageUpload(file, "tree_images");
            this.uploadedImages.push({
              url: result.fullPath,
              name: file.name,
              s3_key: result.key,
            });
          } catch (uploadErr) {
            console.error("Error uploading tree image", uploadErr);
            this.$notify({
              text: uploadErr?.message || this.$t("carbonCredit.manageTrees.fileUploadFailed"),
              type: "error",
            });
          }
        }
      } finally {
        this.uploadingImages = false;
        this.stopLoading();
      }
    },

    removeImage(idx) {
      URL.revokeObjectURL(this.uploadedImages[idx].url);
      this.uploadedImages.splice(idx, 1);
    },

    cancel() {
      this.$router.push({ name: "manageTrees" });
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      if (this.uploadingImages) {
        this.$notify({ text: this.$t("loadingData"), type: "info" });
        return;
      }
      this.saving = true;
      try {
        // Parse GPS coordinates into lat/lon
        let lat = null;
        let lon = null;
        if (this.form.gps_coordinates) {
          const parts = this.form.gps_coordinates.split(",").map(p => p.trim());
          if (parts.length === 2) {
            lat = parseFloat(parts[0]) || null;
            lon = parseFloat(parts[1]) || null;
          }
        }

        const payload = {
          tree_id_by_user: this.form.tree_id_by_user,
          additional_id: this.form.additional_id,
          farm_id: this.form.farm_id,
          zone_id: this.form.zone_id,
          lat,
          lon,
          location_information: this.form.location_information,
          altitude: this.form.altitude?.trim() || null,
          seedling_name: this.form.seedling_name?.trim() || null,
          tree_type_id: this.form.tree_type_id,
          tree_species_id: this.form.tree_species_id,
          date_planted: this.form.date_planted,
          health_condition: this.form.health_condition,
          program_type: this.form.program_type,
          additional_note: this.form.additional_note,
          images: this.uploadedImages
            .filter((img) => img?.s3_key)
            .map((img) => ({ s3_key: img.s3_key })),
        };

        await CarbonCreditService.createTree(payload);
        this.$notify({ text: this.$t('carbonCredit.manageTrees.treeCreatedSuccessfully'), type: "success" });
        this.$router.push({ name: "manageTrees" });
      } catch (err) {
        this.$notify({
          text: err?.response?.data?.message || this.$t('carbonCredit.manageTrees.treeCreateFailed'),
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.dropzone {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover,
  &--active {
    border-color: #0057bd;
    background: #f0f6ff;
  }
}

.image-preview-item {
  position: relative;
  display: inline-block;
  margin-right: 8px;
  margin-bottom: 8px;
}

.remove-image-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  background: white !important;
  border-radius: 50%;
}
</style>
